import { useState } from 'react';
import { useOnMount } from '~/_shared/hooks/useOnMount';
import { GAEventName } from '~/_shared/utils/3rdPartyIntegrations/ga/ga.types';
import {
  popSsoOriginPage, sendAccountCreatedEvent, sendSignupFunnelEvent,
} from '~/_shared/utils/3rdPartyIntegrations/ga/signupFunnel.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { useQuery } from '~/_shared/utils/hooks/useQuery';
import { useUserSign } from '../loginForm/useUserSign';
import { useNavigateAfterLogin } from '../useNavigateAfterLogin';
import { getMsSsoRedirectUri } from './sso.helpers';
import {
  msSsoLogin, SsoLoginResponseCode,
} from './sso.repository';
import { SsoPageComponent } from './ssoPage.component';

export const MS_SECURITY_TOKEN_KEY = 'security_token';
export const MS_SECURITY_TOKEN_ITEM_KEY = 'ms_security_token';
export const MS_SSO_SCOPES = 'openid email profile User.Read';
export const MS_PKCE_CODE_VERIFIER_KEY = 'ms_pkce_code_verifier';

export const MsSsoPageContainer = () => {
  const [t] = useTranslation();
  const query = useQuery();
  const { onLoginSuccess } = useUserSign();

  useNavigateAfterLogin();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);

  const state = query.get('state') || '';
  const code = query.get('code') || '';
  const queryError = query.get('error') || '';

  useOnMount(() => {
    const originPage = popSsoOriginPage();
    const trackReturn = (errorCode?: string) => sendSignupFunnelEvent(GAEventName.SignupSsoReturn, {
      page: originPage,
      method: 'microsoft',
      result: errorCode ? 'error' : 'success',
      errorCode,
    });

    if (queryError) {
      let errorMessage = t('sso.login.error.withCode', { code: '#01' });
      if (queryError === 'access_denied') {
        errorMessage = t('sso.error.permissionsNotGranted');
      }

      trackReturn(`01_${queryError}`);
      setError(errorMessage);
      setIsLoading(false);
      return;
    }

    const performLogin = async () => {
      let errorMessage = undefined;
      let errorCode: string | undefined = undefined;

      const securityToken = new URLSearchParams(JSON.parse(decodeURIComponent(state))).get(MS_SECURITY_TOKEN_KEY) || '';
      const storedSecurityToken = sessionStorage.getItem(MS_SECURITY_TOKEN_ITEM_KEY);

      if (securityToken !== storedSecurityToken || !securityToken || !storedSecurityToken) {
        errorMessage = t('sso.login.error.withCode', { code: '#02' });
        errorCode = '02';
      }
      if (storedSecurityToken) {
        sessionStorage.removeItem(MS_SECURITY_TOKEN_ITEM_KEY);
      }

      // Verify PKCE code verifier exists
      const codeVerifier = sessionStorage.getItem(MS_PKCE_CODE_VERIFIER_KEY);
      if (!codeVerifier && !errorMessage) {
        errorMessage = errorMessage = t('sso.login.error.withCode', { code: '#03' });
        errorCode = '03';
      }

      if (!errorMessage && codeVerifier) {
        try {
          const loginResult = await msSsoLogin({
            code,
            codeVerifier,
            redirectUri: getMsSsoRedirectUri(),
            state,
          });

          if (loginResult.code === SsoLoginResponseCode.Success) {
            onLoginSuccess(loginResult, { isSsoLogin: true });
            if (loginResult.is_new_user) {
              sendAccountCreatedEvent({ page: originPage, method: 'microsoft', emailType: loginResult.email_type });
            }
          }
        }
        catch (e) {
          errorMessage = errorMessage = t('sso.login.error.withCode', { code: '#04' });
          errorCode = '04';
        }
      }

      trackReturn(errorCode);

      if (errorMessage) {
        setError(errorMessage);
        setIsLoading(false);
        return;
      }
    };

    performLogin();

    // we want this to happen only once on mount

  });

  return (
    <SsoPageComponent
      error={error}
      isLoading={isLoading}
    />
  );
};
